"use client";

import { useCallback, useEffect, useState } from "react";
import { AlertIcon, CheckIcon, ClockIcon } from "./icons";

/**
 * Platform `.toast` — the bottom-anchored inverted-dark confirmation bar
 * (design-system/05-atoms.md#toast-sonner, "Platform .toast variant"). Styling
 * lives in ds/base.css; this only owns the show/hide timing, ported from the
 * prototype (2.4s for a confirmation, 3.2s when something needs reading).
 *
 * Transient feedback only — anything the candidate must act on stays inline
 * (field error / device-check row), per the atom's rule.
 */
export type ToastType = "ok" | "error" | "warn";

const ICON: Record<ToastType, React.ReactNode> = {
  ok: <CheckIcon />,
  error: <AlertIcon />,
  warn: <ClockIcon />,
};

type ToastState = { msg: string; type: ToastType; seq: number };

export function useToast() {
  const [state, setState] = useState<ToastState | null>(null);
  const [show, setShow] = useState(false);

  const toast = useCallback((msg: string, type: ToastType = "ok") => {
    setState((prev) => ({ msg, type, seq: (prev?.seq ?? 0) + 1 }));
    setShow(true);
  }, []);

  // Keyed on seq so re-firing the same message restarts the timer.
  useEffect(() => {
    if (!state || !show) return;
    const id = window.setTimeout(
      () => setShow(false),
      state.type === "ok" ? 2400 : 3200
    );
    return () => window.clearTimeout(id);
  }, [state, show]);

  const toastNode = (
    <div
      className={`toast${show ? " show" : ""}${
        state && state.type !== "ok" ? ` ${state.type}` : ""
      }`}
      role="status"
      aria-live="polite"
    >
      {state ? (
        <>
          {ICON[state.type]}
          {state.msg}
        </>
      ) : null}
    </div>
  );

  return { toast, toastNode };
}
